import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { ItemManagementService } from '@app/cem/project/services/item-management.service';
import { LookupService } from "@app/cem/project/services/lookup.service";
import { IStandardConfigDtoState, StdCfgSharedServiceService } from "@app/cem/project/standard-config/std-cfg-shared-service.service";
import { PlexEnvironment } from '@app/env';
import { PxdAgGridCheckboxRendererReadOnly } from '@app/shared/aggrid/pxd-ag-grid-cell-renderers';
import { PxdAgGridNumericEditorComponent } from '@app/shared/aggrid/pxd-ag-grid-numeric-editor';
import { PxdAgEditGridDefaultColumnDef, PxdAgGridColumnTypes, PxdAgGridOptions, PxdAgGridSideBarOptions, PxdAgGridStatusBarOptions } from "@app/shared/aggrid/pxd-ag-grid-options";
import { PxdAgGridSelectEditorComponent } from '@app/shared/aggrid/pxd-ag-grid-select-editor';
import { eQuantityTypes, ProjectTypeId } from '@app/shared/data/project-enums';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { IFunctionDto } from '@app/shared/models/dtos/function';
import { IItemCategoryDto } from '@app/shared/models/dtos/item-category';
import { IItemSubCategoryDto } from '@app/shared/models/dtos/item-sub-category';
import { IProjectTypePhasesAndStepsDto } from '@app/shared/models/dtos/project-type-phases-and-steps';
import { IScopingDependentItemDto } from '@app/shared/models/dtos/scoping-dependent-item';
import { IStandardConfigDto } from "@app/shared/models/dtos/standard-config";
import { IStandardConfigItemDto } from "@app/shared/models/dtos/standard-config-item";
import { IUnitOfMeasureDto } from "@app/shared/models/dtos/unit-of-measure";
import { ItemScopingCategory } from '@app/shared/models/item-scoping-category';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, ColGroupDef, GetDetailRowDataParams, GridApi, GridOptions, GridReadyEvent, IRowNode } from "ag-grid-community";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: 'app-stg-cfg-pad',
  templateUrl: './stg-cfg-pad.component.html',
  styleUrls: ['./stg-cfg-pad.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    AgGridAngular,
  ],
})
export class StgCfgPadComponent implements OnInit {
  public stdCfgSharedServiceService = inject(StdCfgSharedServiceService);
  private lookupService = inject(LookupService);
  private itemManagementService = inject(ItemManagementService);
  private http = inject(HttpClient);
  public environment = inject(PlexEnvironment);
  
  public icons = kendoIcons;
  public editMode: boolean = false;
  public loading: boolean = false;
  selectedFunction: IFunctionDto;
  selectedStandardConfig: IStandardConfigDto;
  unsubscribe$: Subject<boolean> = new Subject();
  unsubscribeStandardConfig$: Subject<boolean> = new Subject();
  public IProjectTypePhasesAndStepsDto: Array<IProjectTypePhasesAndStepsDto> = new Array<IProjectTypePhasesAndStepsDto>();
  public IUnitOfMeasureDtos: Array<IUnitOfMeasureDto> = new Array<IUnitOfMeasureDto>();
  public IItemCategoryDtos: Array<IItemCategoryDto> = new Array<IItemCategoryDto>();
  public IItemSubCategoryDtos: Array<IItemSubCategoryDto> = new Array<IItemSubCategoryDto>();
  public autoGroupColumnDef;
  private EmptyPhaseStep: string = 'Unassigned';

  private potentialParent;
  private gridApi: GridApi;

  constructor() {
    this.autoGroupColumnDef = {
      resizable: true,
      editable: false,
      minWidth: 50,
      cellRendererParams: {
        suppressCount: true,
      },
      cellClassRules: {
        'hover-over': params => params.node === this.potentialParent
      },
    };
  }

  private BASE_URL = this.environment.apiUrl;
  private ItemScopingCategoryList: Array<ItemScopingCategory> = new Array<ItemScopingCategory>();
  private ItemScopingCategoryMap: {};

  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;

    this.stdCfgSharedServiceService.connectItemGridToStdConfigItemGrid(this.gridApi);
    if (this.selectedStandardConfig !== null && this.selectedStandardConfig !== undefined) {
      this.filterPhasesAndSteps();
      await this.loadGrid();
    }
    this.applyAfeColumnState();
  }

  async ngOnInit() {
    this.IItemCategoryDtos = (await this.itemManagementService.findItemTypeDtos()).Results;
    this.IItemSubCategoryDtos = (await this.itemManagementService.findItemSubTypeDtos()).Results;
    await this.initSelectedFunction();
    await this.loadItemScopingCategories();
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
    this.unsubscribeStandardConfig$.next(true);
    this.unsubscribeStandardConfig$.complete();
  }

  private applyAfeColumnState(): void {
    if (!this.gridApi) return;

    const currentDefs = (this.gridApi.getColumnDefs() ?? []) as (ColDef | ColGroupDef)[];

    const newDefs: (ColDef | ColGroupDef)[] = currentDefs.map(def => {
      if ('children' in def) {
        return def;
      }
      const updated: ColDef = { ...def };
      const isCostCodeCostTypeId =
        (updated.colId === 'CostCodeCostTypeId') || (updated.field === 'CostCodeCostTypeId');

      if (isCostCodeCostTypeId) {
        updated.headerName = 'GFCM';
      }

      return updated;
    });

    this.gridApi.setGridOption('columnDefs', newDefs);
    this.gridApi.refreshCells({ columns: ['CostCodeCostTypeId'], force: true });
    this.gridApi.stopEditing(true);
    this.gridApi.refreshClientSideRowModel('aggregate');
  }

  getPhaseNameById = (id: Number) => {
    if (!id) return '';
    let phaseStepId = id;
    let phasesAndStepsDto = this.IProjectTypePhasesAndStepsDto.find(l => l.PhaseStepTypeId === phaseStepId);

    if (phasesAndStepsDto) {
      return phasesAndStepsDto.Phase;
    }
    return '';
  }

  getStepNameById = (id: Number) => {
    if (!id) return '';
    let phaseStepId = id;
    let phasesAndStepsDto = this.IProjectTypePhasesAndStepsDto.find(l => l.PhaseStepTypeId === phaseStepId);
    if (phasesAndStepsDto) {
      return phasesAndStepsDto.Step;
    }
    return '';
  }

  getIUnitOfMeasureDtosById = (id) => {
    if (!id) return '';
    let atype = this.IUnitOfMeasureDtos.find(a => a.UnitOfMeasureId == id);
    if (atype) return atype.Description;
    return '';
  }

  getIItemCategoryDtosById = (id) => {
    if (!id) return '';
    let atype = this.IItemCategoryDtos.find(a => a.ItemCategoryId == id);
    if (atype) return atype.ItemCategoryName;
    return '';
  }

  getIItemSubCatDtosById = (id) => {
    if (!id) return '';
    let atype = this.IItemSubCategoryDtos.find(a => a.ItemSubCategoryId == id);
    if (atype) return atype.ItemSubCategoryName;
    return '';
  }

  getIQtyDtoById = (id) => {
    if (!id) return '';
    let atype = this.stdCfgSharedServiceService.IIQtyTypeDtos.find(a => a.Id == id);
    if (atype) return atype.Name;
    return '';
  }

  getIEquationDtoById = (id) => {
    if (!id) return '';
    let atype = this.stdCfgSharedServiceService.IIEquationDtos.find(a => a.EquationId == id);
    if (atype) return (atype.Description == null ? '' : atype.Description) + ': ' + atype.EquationValue;
    return '';
  }

  public gridOptions: PxdAgGridOptions = {
    enableCharts: true,
    cellSelection: true,
    rowGroupPanelShow: 'never',
    rowSelection: { mode: 'multiRow', groupSelects: 'descendants', checkboxes: true },
    defaultColDef: PxdAgEditGridDefaultColumnDef,
    statusBar: PxdAgGridStatusBarOptions,
    sideBar: PxdAgGridSideBarOptions,
    columnTypes: PxdAgGridColumnTypes,
    isExternalFilterPresent: () => { return true },
    doesExternalFilterPass: node => { return this.filterOutEmptyRows(node) },
    isGroupOpenByDefault: (params) => {
      return true;
    },
    masterDetail: true,
    isRowMaster: (dataItem: IStandardConfigItemDto) => {
      if (dataItem.ItemDto && dataItem.ItemDto.DependentScopingItems && dataItem.ItemDto.DependentScopingItems.length > 0) {
        return true;
      }
      return false;
    },
    detailRowAutoHeight: true,
    detailCellRendererParams: {
      detailGridOptions: {
        rowSelection: { enableClickSelection: false },
        columnDefs: [
          { field: 'ItemNumber' },
          { field: 'Name', headerName: 'Description' },
          {
            field: 'CostCodeCostTypeId'
            , headerName: 'Cost Code Cost Type'
            , editable: false
            , cellRenderer: params => {
              if (params.data && params.data.CostCodeCostTypeDto) {
                return (params.data.CostCodeCostTypeDto.CostCodeDto.CostCodeValue + '.' + params.data.CostCodeCostTypeDto.CostTypeDto.CostTypeValue)
              } else {
                return '';
              }
            }
          },
          {
            field: 'PrimaryUOM', cellRenderer: params => {
              if (params.data) {
                return this.getIUnitOfMeasureDtosById(params.data.PrimaryUOM);
              } else {
                return '';
              }
            }
          },
          { field: 'Quantity', editable: false },
        ] as ColDef[],
        defaultColDef: {
          sortable: true,
          flex: 1,
          resizable: true
        }
      } as GridOptions,
      getDetailRowData: (params: GetDetailRowDataParams) => {
        if (params.data.ItemDto) {
          params.successCallback(params.data.ItemDto.DependentScopingItems);
        }
      }
    },
    columnDefs: [
      {
        field: 'PhaseStepSortOrder'
        , rowGroup: false
        , hide: true
        , sort: null
      },

      {
        field: 'Phase'
        , rowGroup: true
        , hide: false
        , headerName: 'Phase'
        , editable: false
        , width: 100
        , comparator: (valueA, valueB) => {
          const orderA = this.getPhaseOrder(valueA);
          const orderB = this.getPhaseOrder(valueB);
          return orderA - orderB;
        }
      }
      , {
        field: 'Step'
        , rowGroup: true
        , hide: false
        , headerName: 'Step'
        , editable: false
        , width: 100
        , comparator: (valueA, valueB, nodeA, nodeB) => {
          const phaseA = nodeA?.data?.Phase ?? nodeA?.parent?.key ?? '';
          const phaseB = nodeB?.data?.Phase ?? nodeB?.parent?.key ?? '';
          const orderA = this.getStepOrder(phaseA, valueA);
          const orderB = this.getStepOrder(phaseB, valueB);
          return orderA - orderB;
        }
      },

      {
        field: 'ItemDto.ItemNumber'
        , headerName: 'Item Number'
        , editable: false
        , width: 130
        , cellClassRules: {
          'hover-over': params => params.node === this.potentialParent,
        }
        , rowDrag: function (params) {
          return (!params.node.group && params.node.data.ItemId !== undefined);
        },
      }
      , {
        field: 'Name'
        , headerName: 'Description'
        , editable: false
        , width: 145
        , cellRenderer: params => {
          return params.value;
        }
        , cellClassRules: {
          'hover-over': params => params.node === this.potentialParent,
        }
        , valueGetter: params => {
          let val = (params.data !== null && params.data !== undefined && params.data.ItemDto !== null && params.data.ItemDto !== undefined) ? params.data.ItemDto.Name : '';
          return val;
        }
      }
      , {
        field: 'ItemCategoryId'
        , headerName: 'Category'
        , hide: true
        , editable: false
        , cellRenderer: params => {
          return params.value;
        },
        valueGetter: params => {
          if (params.data && params.data.ItemDto && params.data.ItemDto.ItemCategoryId) {
            return this.getIItemCategoryDtosById(params.data.ItemDto.ItemCategoryId);
          } else {
            return '';
          }
        },
        width: 110
      }
      , {
        field: 'ItemSubCategoryId'
        , headerName: 'SubCat'
        , hide: true
        , editable: false
        , cellRenderer: params => {
          return params.value;
        },
        valueGetter: params => {
          if (params.data && params.data.ItemDto && params.data.ItemDto.ItemSubCategoryId) {
            return this.getIItemSubCatDtosById(params.data.ItemDto.ItemSubCategoryId);
          } else {
            return '';
          }
        },
        width: 100
      }
      , {
        field: 'ItemScopingCategoryId'
        , rowGroup: false
        , headerName: 'Scoping Category'
        , hide: true
        , editable: false
        , cellRenderer: 'agGroupCellRenderer',
        valueGetter: params => {
          if (params.data && params.data.ItemDto && params.data.ItemDto.ItemScopingCategoryId) {
            return this.ItemScopingCategoryMap[params.data.ItemDto.ItemScopingCategoryId];
          } else {
            return '';
          }
        },
        width: 125
      }
      , {
        field: 'CostCodeCostTypeId'
        , headerName: 'Cost Code Cost Type'
        , editable: false
        , cellRenderer: params => {
          if (params.data && params.data.ItemDto && params.data.ItemDto.CostCodeCostTypeDto) {
            return (params.data.ItemDto.CostCodeCostTypeDto.CostCodeDto.CostCodeValue + ' - ' + params.data.ItemDto.CostCodeCostTypeDto.CostCodeDto.CostCodeDescription);
          } else {
            return '';
          }

        },
        filterParams: {
          valueGetter: params => {
            if (params.data && params.data.ItemDto && params.data.ItemDto.CostCodeCostTypeDto) {
              return (params.data.ItemDto.CostCodeCostTypeDto.CostCodeDto.CostCodeValue + ' - ' + params.data.ItemDto.CostCodeCostTypeDto.CostCodeDto.CostCodeDescription);
            } else {
              return '';
            }
          }
        },
        valueGetter: params => {
          if (params.data && params.data.ItemDto && params.data.ItemDto.CostCodeCostTypeDto) {
            return (params.data.ItemDto.CostCodeCostTypeDto.CostCodeDto.CostCodeValue + ' - ' + params.data.ItemDto.CostCodeCostTypeDto.CostCodeDto.CostCodeDescription);
          } else {
            return '';
          }
        },
        width: 100
        , cellClassRules: {
          'hover-over': params => params.node === this.potentialParent,
        }
      }
      , {
        field: 'PrimaryUOM', headerName: 'UOM', editable: false
        , cellRenderer: params => {
          if (params.value) {
            return params.value;
          } else if (params.data && params.data.ItemDto) {
            return this.getIUnitOfMeasureDtosById(params.data.ItemDto.PrimaryUOM);
          } else {
            return '';
          }

        },
        filterParams: {
          valueGetter: params => {
            if (params.data && params.data.ItemDto) {
              return this.getIUnitOfMeasureDtosById(params.data.ItemDto.PrimaryUOM);
            } else {
              return '';
            }
          }
        },
        width: 90,
        valueGetter: params => {
          if (params.data && params.data.ItemDto) {
            return this.getIUnitOfMeasureDtosById(params.data.ItemDto.PrimaryUOM);
          } else {
            return '';
          }
        }
        ,
        cellEditorSelector: params => {
          return {
            component: PxdAgGridSelectEditorComponent,
            params: {

              values: Array.from(this.IUnitOfMeasureDtos).map(d => {
                return {
                  value: d.UnitOfMeasureId,
                  text: d.Description
                };
              }),
              value: params.data.ItemDto.PrimaryUOM
            }
          };
        }, cellClassRules: {
          'hover-over': params => params.node === this.potentialParent,
        }
      },
      {
        field: 'QuantityTypeId', headerName: 'Qty Type', editable: function (params) {
          return (!params.node.group && params.node.data.ItemId !== undefined);
        }
        , cellRenderer: params => {
          if (params.value) {
            return params.value;
          } else if (params.data && params.data.ItemDto) {
            return this.getIQtyDtoById(params.data.QuantityTypeId);
          } else {
            return '';
          }
        },
        filterParams: {
          valueGetter: params => {
            if (params.data && params.data.ItemDto) {
              return this.getIQtyDtoById(params.data.QuantityTypeId);
            } else {
              return '';
            }
          }
        },
        width: 100,
        valueGetter: params => {
          if (params.data && params.data.ItemDto) {
            return this.getIQtyDtoById(params.data.QuantityTypeId);
          } else {
            return '';
          }
        }
        ,
        cellEditorSelector: params => {
          return {
            component: PxdAgGridSelectEditorComponent,
            cellEditorPopup: true,
            params: {

              values: Array.from(this.stdCfgSharedServiceService.IIQtyTypeDtos).map(d => {
                return {
                  value: d.Id,
                  text: d.Name
                };
              }),
              value: params.data.QuantityTypeId,
            }
          };
        }, cellClassRules: {
          'hover-over': params => params.node === this.potentialParent,
        }
      }

      , {
        field: 'QuantityValue',
        headerName: 'Qty Detail',
        width: 110,
        //NOTE: Sometimes, this is EquationId, sometimes it is Quantity
        valueGetter: params => {
          if (params.data && params.data.QuantityTypeId === eQuantityTypes.Number) {
            return params.data.Quantity;
          } else if (params.data && params.data.QuantityTypeId === eQuantityTypes.Calculation) {
            return params.data.EquationId;
          } else {
            return '';
          }
        }
        ,
        valueSetter: params => {
          if (params.data && params.data.QuantityTypeId === eQuantityTypes.Number) {
            params.data.Quantity = params.newValue;

          } else if (params.data.QuantityTypeId === eQuantityTypes.Calculation) {
            params.data.EquationId = params.newValue;
          }
          return true;
        }
        ,
        cellEditorSelector: params => {
          if (params.data && params.data.QuantityTypeId === eQuantityTypes.Number) {
            return { component: PxdAgGridNumericEditorComponent };
          } else if (params.data && params.data.QuantityTypeId === eQuantityTypes.Calculation) {
            return {
              component: PxdAgGridSelectEditorComponent,
              params: {
                showEquationTemplate: true,
                values: Array.from(this.stdCfgSharedServiceService.IIEquationDtos).map(d => {
                  return {
                    value: d.EquationId,
                    description: (d.Description == null ? '' : d.Description + ' :'),
                    text: d.EquationValue
                  };
                }),
                value: params.data.EquationId,
                popupWidth: '90vw'
              }
            };
          }
        },
        cellEditorParams: {
          decimals: 5, format: "n5"
        },
        tooltipValueGetter: (params) => {
          if (params.data && params.data.QuantityTypeId === eQuantityTypes.Number) {
            return params.data.Quantity;
          } else if (params.data && params.data.QuantityTypeId === eQuantityTypes.Calculation) {
            let val = this.getIEquationDtoById(params.data.EquationId);
            return val;
          } else if (params.value) {
            return params.value;
          } else {
            return '';
          }
        },
        cellRenderer: params => {
          if (params.data && params.data.QuantityTypeId === eQuantityTypes.Number) {
            return params.data.Quantity;
          } else if (params.data && params.data.QuantityTypeId === eQuantityTypes.Calculation) {
            let val = this.getIEquationDtoById(params.data.EquationId);
            return val;
          } else if (params.value) {
            return params.value;
          } else {
            return '';
          }
        },

        editable: function (params) {
          return (!params.node.group && params.node.data.ItemId !== undefined) && (params.data.QuantityTypeId === eQuantityTypes.Number || params.data.QuantityTypeId === eQuantityTypes.Calculation);
        },
      },
      {
        field: 'ItemDto.IsActive',
        editable: false,
        singleClickEdit: true,
        headerName: 'Active',
        cellRendererSelector: params => {
          if (params.data && params.data.ItemDto && params.data.ItemDto.IsActive != null) {
            return { component: PxdAgGridCheckboxRendererReadOnly };
          }
        },
        width: 75
      }

    ]
  };

  //this method is for conditionally hiding any of the default rows that we push in order to force the Phase / Step groupings to dipslay when there are no items in that particular grouping.
  private filterOutEmptyRows(node: IRowNode): boolean {
    const items = this.selectedStandardConfig.StandardConfigurationItemDtos;

    if (!items) {
      return true;
    }

    const otherItemsExist = items.some(item =>
      item.Phase === node.data.Phase && item.Step === node.data.Step && item.ItemDto !== null
    );

    return otherItemsExist ? node.data.ItemDto !== null : true;
  }

  private getPhaseOrder(phase: string): number {
    if (!phase) return Number.MAX_SAFE_INTEGER;
    if (phase === 'CONST') return -1;

    let min = Number.MAX_SAFE_INTEGER;
    for (const x of this.IProjectTypePhasesAndStepsDto) {
      if (x.Phase === phase) {
        min = Math.min(min, x.SortOrder ?? Number.MAX_SAFE_INTEGER);
      }
    }
    return min;
  }

  private getStepOrder(phase: string, step: string): number {
    if (!phase || !step) return Number.MAX_SAFE_INTEGER;
    const pst = this.IProjectTypePhasesAndStepsDto.find(x => x.Phase === phase && x.Step === step);
    return pst?.SortOrder ?? Number.MAX_SAFE_INTEGER;
  }

  public sortByPhaseStepOrderAsc() {
    this.gridApi.applyColumnState({
      state: [{ colId: 'Phase', sort: 'asc', sortIndex: 0 }, { colId: 'Step', sort: 'asc', sortIndex: 1 }],
      defaultState: { sort: null },
    });
  }

  private executePhaseStep: IProjectTypePhasesAndStepsDto = {
    Phase: 'EXECUTE',
    Step: 'ACTIVITY',
    PhaseStepTypeId: 42,
    SortOrder: 0,
    ProjectTypeId: null
  }

  filterPhasesAndSteps(): void {
    /*
      1) on selection of Standard Config that is not new
      2) on save of new standard config
    */
    if (!this.selectedStandardConfig.ProjectTypeId) {
      this.IProjectTypePhasesAndStepsDto = [this.executePhaseStep];
    } else {
      let filtered = this.stdCfgSharedServiceService.IProjectTypePhasesAndStepsDto
        .filter(s => s.ProjectTypeId === this.selectedStandardConfig.ProjectTypeId);

      if (filtered.length == 0) {
        filtered = [this.executePhaseStep];
      }
      this.IProjectTypePhasesAndStepsDto = filtered;
    }
  }

  async initSelectedFunction() {
    this.stdCfgSharedServiceService.getSelectedFunctionDto().pipe(takeUntil(this.unsubscribe$)).subscribe(response => {
      this.selectedFunction = response;
    }
    );

    this.stdCfgSharedServiceService.getSelectedStandardConfigDto().pipe(takeUntil(this.unsubscribeStandardConfig$)).subscribe(async response => {
      this.selectedStandardConfig = response;
      if (this.selectedStandardConfig !== null && this.selectedStandardConfig !== undefined && this.gridApi && this.gridApi.getColumnDefs() != null) {
        this.filterPhasesAndSteps();
        await this.loadGrid();
      }
    }
    );

    this.IUnitOfMeasureDtos = (await this.lookupService.findUnitOfMeasureDtos()).Results;
  }

  async loadGrid() {
    if (!this.gridApi) return;

    if (this.selectedStandardConfig) {
      this.toggleRowGrouping(this.selectedStandardConfig.ProjectTypeId);
    }
    if (this.selectedStandardConfig.ProjectTypeId == ProjectTypeId.TankBattery) {
      let dependentItems = await this.getDependentItems(this.selectedStandardConfig.StandardConfigurationId);
      this.selectedStandardConfig.StandardConfigurationItemDtos
        .forEach(x => {
          if (x.ItemDto) {
            let item = dependentItems.find(y => y.ScopingItemId === x.ItemId);
            x.ItemDto.DependentScopingItems = item != null ? item.DependentScopingItems : null;
          }
        });
    }
    if (this.stdCfgSharedServiceService.ActiveIStandardConfigDtoState.has(this.selectedStandardConfig.StandardConfigurationId)) {
      let stdConfig = this.stdCfgSharedServiceService.ActiveIStandardConfigDtoState.get(this.selectedStandardConfig.StandardConfigurationId);

      this.selectedStandardConfig.StandardConfigurationItemDtos = stdConfig.AllIStandardConfigItemDtos;
      this.gridApi.setGridOption('rowData', this.selectedStandardConfig.StandardConfigurationItemDtos);
    } else {
      this.IProjectTypePhasesAndStepsDto.sort(function (a, b) {
        return a.SortOrder - b.SortOrder;
      });

      this.selectedStandardConfig.StandardConfigurationItemDtos.forEach(sci => {
        if (!sci.Phase) {
          let pst = this.stdCfgSharedServiceService.IProjectTypePhasesAndStepsDto.find(pt => pt.PhaseStepTypeId === sci.PhaseStepTypeId);
          if (pst !== null && pst !== undefined) {
            sci.Phase = pst.Phase;
            sci.Step = pst.Step;
            sci.PhaseStepSortOrder = pst.SortOrder;
          } else {
            sci.Phase = this.EmptyPhaseStep;
            sci.Step = this.EmptyPhaseStep;
            sci.PhaseStepSortOrder = 0;
          }
        }
      });

      let combinedItems = (this.IProjectTypePhasesAndStepsDto.map(pt => {

        return <IStandardConfigItemDto>
          {
            StandardConfigurationId: this.selectedStandardConfig.StandardConfigurationId,
            PhaseStepTypeId: pt.PhaseStepTypeId,
            Phase: pt.Phase,
            Step: pt.Step,
            IsActive: true,
            ItemDto: null,
            PhaseStepSortOrder: pt.SortOrder
          };
      }));

      combinedItems.push(
        <IStandardConfigItemDto>{
          StandardConfigurationId: this.selectedStandardConfig.StandardConfigurationId,
          PhaseStepTypeId: null,
          Phase: this.EmptyPhaseStep,
          Step: this.EmptyPhaseStep,
          IsActive: true,
          ItemDto: null,
          PhaseStepSortOrder: 0
        });

      combinedItems.push(...this.selectedStandardConfig.StandardConfigurationItemDtos);

      combinedItems.sort((a, b) => a.PhaseStepSortOrder - b.PhaseStepSortOrder);

      this.selectedStandardConfig.StandardConfigurationItemDtos = combinedItems;

      this.stdCfgSharedServiceService.ActiveIStandardConfigDtoState.set(this.selectedStandardConfig.StandardConfigurationId, <IStandardConfigDtoState>{
        AllIStandardConfigItemDtos: [...this.selectedStandardConfig.StandardConfigurationItemDtos],
        IStandardConfigDto: this.selectedStandardConfig,
      });
      this.gridApi.setGridOption('rowData', this.selectedStandardConfig.StandardConfigurationItemDtos);
    }

    this.gridApi.sizeColumnsToFit();

    this.sortByPhaseStepOrderAsc();

  }

  toggleRowGrouping(projectTypeId: number) {
    var columnDefs: ColDef[] = this.gridApi.getColumnDefs();

    var itemScopingCategoryIdColumn = columnDefs.find(def => def.field == 'ItemScopingCategoryId');
    var phaseColumn = columnDefs.find(def => def.field == 'Phase');
    var stepColumn = columnDefs.find(def => def.field == 'Step');
    if (projectTypeId != undefined && projectTypeId == ProjectTypeId.TankBattery) {
      this.gridOptions.groupDefaultExpanded = 1;
      itemScopingCategoryIdColumn.rowGroup = false;
      itemScopingCategoryIdColumn.hide = false;
      phaseColumn.rowGroup = false;
      phaseColumn.hide = true;
      stepColumn.rowGroup = true;
      stepColumn.hide = true;
      this.gridApi.moveColumnByIndex(itemScopingCategoryIdColumn.pivotIndex, 0);
    } else {
      this.gridOptions.groupDefaultExpanded = -1;
      itemScopingCategoryIdColumn.rowGroup = false;
      itemScopingCategoryIdColumn.hide = true;
      phaseColumn.rowGroup = true;
      phaseColumn.hide = false;
      stepColumn.rowGroup = true;
      stepColumn.hide = false;
    }
    this.gridApi.setGridOption('columnDefs', columnDefs);
  }

  onRowDragEnd(event) {
    if (this.selectedStandardConfig?.StandardConfigurationId == null) return;
    let overNode = event.overNode;
    let phase = null;
    let step = null;
    let phaseStepTypeId = null;
    let PhaseStepSortOrder = null;

    this.setPotentialParentForNode(event.api, null);

    if (overNode) {
      if (overNode.data === undefined) {
        return;
      }

      if (!(overNode.data.Step && overNode.data.Phase)) {
        return;
      }

      phase = overNode.data.Phase;
      step = overNode.data.Step;
      phaseStepTypeId = overNode.data.PhaseStepTypeId;
      PhaseStepSortOrder = overNode.data.PhaseStepSortOrder;
    }

    let count = this.selectedStandardConfig.StandardConfigurationItemDtos.filter(s => s.ItemId === event.node.data.ItemId && s.Phase === phase && s.Step === step);
    if (!(count && count.length === 0)) {
      return;
    }

    if ('StandardConfigurationId' in event.node.data) {
      let update = event.node.data;
      update.Phase = phase;
      update.Step = step;
      update.PhaseStepTypeId = phaseStepTypeId;
      update.PhaseStepSortOrder = PhaseStepSortOrder;

      this.stdCfgSharedServiceService.editMode = true;
      this.stdCfgSharedServiceService.editedEntities.set(this.selectedStandardConfig.StandardConfigurationId, this.selectedStandardConfig);
      this.stdCfgSharedServiceService.AddOrEditItem(update.StandardConfigurationItemId, update);

      this.gridApi.applyTransaction({ update: [event.node.data] });
    } else {
      let newIStandardConfigItemDto = <IStandardConfigItemDto>
        {
          StandardConfigurationItemId: this.stdCfgSharedServiceService.newIStandardConfigItemDtoId--,
          StandardConfigurationId: this.selectedStandardConfig.StandardConfigurationId,
          ItemId: event.node.data.ItemId,
          ItemDto: event.node.data,
          QuantityTypeId: 1,
          Quantity: 1,
          PhaseStepTypeId: phaseStepTypeId,
          PhaseStepSortOrder: PhaseStepSortOrder,
          Phase: phase,
          Step: step,
          IsActive: true
        };

      this.stdCfgSharedServiceService.editMode = true;
      this.stdCfgSharedServiceService.editedEntities.set(this.selectedStandardConfig.StandardConfigurationId, this.selectedStandardConfig);
      this.stdCfgSharedServiceService.AddOrEditItem(newIStandardConfigItemDto.StandardConfigurationItemId, newIStandardConfigItemDto);

      this.selectedStandardConfig.StandardConfigurationItemDtos.push(newIStandardConfigItemDto);
      this.gridApi.applyTransaction({ add: [newIStandardConfigItemDto] });
    }
    this.gridApi.clearFocusedCell();
  }

  onRowDragMove(event) {
    this.setPotentialParentForNode(event.api, event.overNode);
  }

  onRowDragLeave(event) {
    this.setPotentialParentForNode(event.api, null);
  }

  setPotentialParentForNode(api, overNode) {
    let newPotentialParent;
    if (overNode) {
      newPotentialParent = overNode;

    } else {
      newPotentialParent = null;
    }
    let alreadySelected = this.potentialParent === newPotentialParent;
    if (alreadySelected) {
      return;
    }
    let rowsToRefresh = [];
    if (this.potentialParent) {
      rowsToRefresh.push(this.potentialParent);
    }
    if (newPotentialParent) {
      rowsToRefresh.push(newPotentialParent);
    }
    this.potentialParent = newPotentialParent;
    this.refreshRows(api, rowsToRefresh);
  }

  refreshRows(api, rowsToRefresh) {
    let params = {
      rowNodes: rowsToRefresh,
      force: true,
    };
    api.refreshCells(params);
  }

  public async cancelChanges() {
    await this.stdCfgSharedServiceService.cancelNewStandardConfigDto();
  }

  removeItem() {
    this.editMode = true;
    this.stdCfgSharedServiceService.editMode = true;
    //filter out selected ghost rows
    const selectedRows = this.gridApi.getSelectedRows().filter(r => r.ItemId !== undefined);
    this.stdCfgSharedServiceService.editedEntities.set(this.selectedStandardConfig.StandardConfigurationId, this.selectedStandardConfig);
    for (let i = 0; i < selectedRows.length; i++) {
      this.stdCfgSharedServiceService.RemoveItem(selectedRows[i].StandardConfigurationItemId, selectedRows[i]);
      this.selectedStandardConfig.StandardConfigurationItemDtos = this.selectedStandardConfig.StandardConfigurationItemDtos.filter(obj => obj.StandardConfigurationItemId !== selectedRows[i].StandardConfigurationItemId);
    }
    this.gridApi.applyTransaction({ remove: selectedRows });
  }

  onCellValueChanged(e) {
    if (e.oldValue == e.newValue) return;

    if (e.colDef && e.colDef.field === 'QuantityTypeId') {
      e.data.QuantityValue = null;
      e.data.Quantity = null;
      e.data.EquationId = null;
    }

    this.stdCfgSharedServiceService.editMode = true;
    this.stdCfgSharedServiceService.editedEntities.set(this.selectedStandardConfig.StandardConfigurationId, this.selectedStandardConfig);
    this.stdCfgSharedServiceService.AddOrEditItem(e.data.StandardConfigurationItemId, e.data);
    this.gridApi.applyTransaction({ update: [e.data] });
  }

  public async saveChanges() {
    this.loading = true;
    await this.stdCfgSharedServiceService.saveChanges().then(async r => {
      this.loading = false;
      if (this.selectedStandardConfig !== null && this.selectedStandardConfig !== undefined && this.selectedStandardConfig.StandardConfigurationId <= 0) {
        this.filterPhasesAndSteps();
        await this.loadGrid();
      }
    });
  }

  async loadItemScopingCategories() {
    let url = `${this.BASE_URL}ItemScopingCategories?$select=ItemScopingCategoryId,Description`;
    let data: ItemScopingCategory[] = (await this.http.get<any>(url).toPromise()).value;
    this.ItemScopingCategoryList = data;
    this.ItemScopingCategoryMap = new Map();
    this.ItemScopingCategoryList.map(x => this.ItemScopingCategoryMap[x.ItemScopingCategoryId] = x.Description);
  }

  async getDependentItems(id: number): Promise<IScopingDependentItemDto[]> {
    let result = await this.itemManagementService.getScopingDependentItems(id);
    return result;
  }
}